import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ConfigService } from './config.service';

// --- Remote place addresses -------------------------------------------------
// A team name can be a device-qualified ADDRESS: "{deviceId}:{team}" — a team
// living on one of the user's attached desktops (Presence). Callers stay
// transport-blind: they pass the address as the team and this service resolves
// it, routing supported operations through /remote/{device}/… and degrading
// unsupported ones to safe empties (never letting a device-qualified name leak
// into a local endpoint). See docs/plans/desktop-remote-access.md.
export interface PlaceAddress {
  device: string;
  team: string;
}

export function parsePlaceAddress(team?: string | null): PlaceAddress | null {
  if (!team) return null;
  const i = team.indexOf(':');
  if (i <= 0 || i === team.length - 1) return null;
  return { device: team.slice(0, i), team: team.slice(i + 1) };
}

export namespace MuxSessionService {
  export interface Session {
    id: string;
    name?: string;
    display_name: string;
    created_at: string;
    updated_at: string;
    agent_id: string;
    metadata: Record<string, any>;
  }

  export interface Message {
    id?: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp?: string;
    metadata?: Record<string, any>;
    pinned?: boolean;
  }

  export interface PaginatedMessages {
    messages: Message[];
    total: number;
    has_more: boolean;
  }

  export interface PinnedMessage {
    id: string;
    content: string;
    role: string;            // session pins
    direction?: string;      // channel pins: 'in' (to the agent) | 'out'
    from?: string;           // channel pins: author
    timestamp: string;
    metadata?: Record<string, any>;
    expanded?: boolean;  // UI state for show-more toggle
  }

  export type GroupedSessions = [string, Session[]][];

  // --- Channel navigation ---

  export interface WorkSession {
    session_id: string;
    title: string;
    agent_id: string;
    status: 'active' | 'completed' | 'abandoned';
    created_at: string;
  }

  export interface NavChannel {
    id: string;
    name: string;
    type: string;
    team_id: string | null;
    agent_id: string | null;
    parent_channel_id?: string;
    folder_path?: string;
    work_sessions?: WorkSession[];
    /** A conversation with someone else's agent (standing orders). The id is
     *  this user's window onto a channel in the host's store: `trust~host~agent`. */
    trust?: { host: string; agent_name: string; about: string; trust_id: string; state: string; notice: string };
    unread?: number;
  }

  /** A sub-agent a CLI agent launched in the background; it outlives the turn. */
  export interface BackgroundAgent {
    id: string;
    type: string;          // general-purpose, Explore, fork, …
    description: string;
    started_ms: number;
    last_ms: number;
    agent_id?: string;     // the channel agent that owns it
  }

  /** What a background agent did since a byte offset, in the chat's own markup. */
  export interface BackgroundAgentTranscript {
    agent_id: string;
    prompt: string | null;      // the task it was given (first read only)
    content: string;            // text, [THINKING] blocks and tool cards to append
    next: number;               // offset for the following read
    finished: boolean | null;   // null = nothing new, state unchanged
    skipped: number;            // bytes of a long run not rendered on the first read
    type?: string | null;
    description?: string | null;
  }

  export interface NavigationTree {
    personal_channels: NavChannel[];
    dm_channels: NavChannel[];
    team: { id: string; name: string; channels: NavChannel[] } | null;
    /** Agents other people have opened to this user and they have started talking to. */
    guest_channels?: NavChannel[];
  }

  export interface RemoteDevice {
    device_id: string;
    name: string;
    teams: string[];
    online: boolean;
  }

  export interface ChannelMessage {
    id: string;
    content: string;
    timestamp: string;
    direction: 'in' | 'out';
    role: 'user' | 'assistant';
    from: string;
    read?: boolean;
    delivered?: boolean;
    pinned?: boolean;
    content_truncated?: boolean;
    content_length?: number;
    reactions?: { emoji: string; reactor: 'user' | 'assistant'; timestamp: string }[];
    metadata?: Record<string, any>;
    file_path?: string; // Full filesystem path to the message file
  }
}

@Injectable({
  providedIn: 'root'
})
export class MuxSessionService {
  private sessions: MuxSessionService.Session[] = [];
  private currentSessionId: string | null = null;

  // Observable for session list updates
  private sessionsSubject = new BehaviorSubject<MuxSessionService.GroupedSessions>([]);
  public sessions$ = this.sessionsSubject.asObservable();

  // Observable for current session changes
  private currentSessionSubject = new BehaviorSubject<string | null>(null);
  public currentSession$ = this.currentSessionSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Load all sessions for an agent
   */
  async loadSessions(agentId: string, searchQuery?: string): Promise<MuxSessionService.Session[]> {
    try {
      let url = `${ConfigService.muxUrl}/agents/${agentId}/sessions`;
      if (searchQuery) {
        url += `?q=${encodeURIComponent(searchQuery)}`;
      }

      this.sessions = await lastValueFrom(
        this.http.get<MuxSessionService.Session[]>(url)
      );

      // Emit grouped sessions
      this.sessionsSubject.next(this.getGroupedSessions());

      return this.sessions;
    } catch (error) {
      console.error('Failed to load sessions from lit-mux:', error);
      return [];
    }
  }

  /**
   * Group sessions by time period (Today, Yesterday, Last 7 Days, etc.)
   */
  getGroupedSessions(): MuxSessionService.GroupedSessions {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    const groups: Record<string, MuxSessionService.Session[]> = {
      'Today': [],
      'Yesterday': [],
      'Last 7 Days': [],
      'Last 30 Days': [],
      'Older': []
    };

    this.sessions.forEach(session => {
      const sessionDate = new Date(session.updated_at);
      const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

      if (sessionDay.getTime() === today.getTime()) {
        groups['Today'].push(session);
      } else if (sessionDay.getTime() === yesterday.getTime()) {
        groups['Yesterday'].push(session);
      } else if (sessionDate >= lastWeek) {
        groups['Last 7 Days'].push(session);
      } else if (sessionDate >= lastMonth) {
        groups['Last 30 Days'].push(session);
      } else {
        groups['Older'].push(session);
      }
    });

    // Convert to array of tuples, filtering out empty groups
    return Object.entries(groups).filter(([_, sessions]) => sessions.length > 0) as MuxSessionService.GroupedSessions;
  }

  /**
   * Get cached sessions list
   */
  getSessions(): MuxSessionService.Session[] {
    return this.sessions;
  }

  /**
   * Get current session ID
   */
  getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Create a new session
   */
  async createSession(agentId: string, backends: string[], name?: string, metadata?: Record<string, any>): Promise<MuxSessionService.Session | null> {
    try {
      const session = await lastValueFrom(
        this.http.post<MuxSessionService.Session>(`${ConfigService.muxUrl}/sessions`, {
          backends,
          agent_id: agentId,
          name,
          metadata
        })
      );

      this.sessions.unshift(session);
      this.currentSessionId = session.id;
      this.currentSessionSubject.next(session.id);
      this.sessionsSubject.next(this.getGroupedSessions());

      return session;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }

  /**
   * Load messages for a specific session
   */
  async loadSession(sessionId: string, limit: number = 50, offset: number = 0, agentId?: string): Promise<MuxSessionService.PaginatedMessages> {
    try {
      let url = `${ConfigService.muxUrl}/sessions/${sessionId}/messages`;
      const params = new URLSearchParams();
      if (limit) {
        params.append('limit', limit.toString());
        params.append('offset', offset.toString());
      }
      if (agentId) {
        params.append('agent_id', agentId);
      }
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const data = await lastValueFrom(
        this.http.get<MuxSessionService.Message[] | MuxSessionService.PaginatedMessages>(url)
      );

      this.currentSessionId = sessionId;
      this.currentSessionSubject.next(sessionId);

      // Handle both paginated and legacy response formats
      if (Array.isArray(data)) {
        return {
          messages: data,
          total: data.length,
          has_more: false
        };
      }
      return data;
    } catch (error) {
      console.error('Failed to load session:', error);
      throw error;
    }
  }

  /**
   * Rename a session
   */
  async renameSession(sessionId: string, newName: string): Promise<MuxSessionService.Session | null> {
    try {
      const updatedSession = await lastValueFrom(
        this.http.patch<MuxSessionService.Session>(`${ConfigService.muxUrl}/sessions/${sessionId}`, {
          name: newName
        })
      );

      const index = this.sessions.findIndex(s => s.id === sessionId);
      if (index !== -1) {
        this.sessions[index] = updatedSession;
        this.sessionsSubject.next(this.getGroupedSessions());
      }

      return updatedSession;
    } catch (error) {
      console.error('Failed to rename session:', error);
      throw error;
    }
  }

  /**
   * Delete a session
   */
  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      await lastValueFrom(
        this.http.delete(`${ConfigService.muxUrl}/sessions/${sessionId}`)
      );

      this.sessions = this.sessions.filter(s => s.id !== sessionId);
      this.sessionsSubject.next(this.getGroupedSessions());

      // Return true if we deleted the current session
      return this.currentSessionId === sessionId;
    } catch (error: any) {
      if (error?.status === 423) throw error;  // refused (legal hold): the caller shows why
      console.error('Failed to delete session:', error);
      throw error;
    }
  }

  /**
   * Delete multiple sessions
   */
  async deleteSessions(sessionIds: string[]): Promise<void> {
    await Promise.all(sessionIds.map(id => this.deleteSession(id)));
  }

  /**
   * Save last selected session for an agent (persists across refresh)
   */
  saveLastSessionForAgent(agentId: string, sessionId: string): void {
    localStorage.setItem(`lit_mux_last_session_${agentId}`, sessionId);
  }

  /**
   * Get last selected session for an agent
   */
  getLastSessionForAgent(agentId: string): string | null {
    return localStorage.getItem(`lit_mux_last_session_${agentId}`);
  }

  /**
   * Add a message to a session without triggering AI response
   * Used for widget events, screenshots, system notes, etc.
   */
  async addMessage(
    sessionId: string,
    content: string,
    role: 'user' | 'assistant' | 'system' = 'system',
    metadata?: Record<string, any>
  ): Promise<MuxSessionService.Message> {
    try {
      const result = await lastValueFrom(
        this.http.post<MuxSessionService.Message>(
          `${ConfigService.muxUrl}/sessions/${sessionId}/messages`,
          { content, role, metadata }
        )
      );
      return result;
    } catch (error) {
      console.error('Failed to add message to session:', error);
      throw error;
    }
  }

  /**
   * Update message metadata (e.g., add screenshot to widget message)
   */
  async updateMessageMetadata(
    sessionId: string,
    messageId: string,
    metadata: Record<string, any>
  ): Promise<void> {
    try {
      await lastValueFrom(
        this.http.patch(
          `${ConfigService.muxUrl}/sessions/${sessionId}/messages/${messageId}`,
          { metadata }
        )
      );
    } catch (error) {
      console.error('Failed to update message metadata:', error);
      throw error;
    }
  }

  /**
   * Compact a session (summarize to reduce context)
   */
  async compactSession(sessionId: string, instructions?: string): Promise<{ pre_tokens?: number; message: string }> {
    try {
      const body: { instructions?: string } = {};
      if (instructions) {
        body.instructions = instructions;
      }

      const result = await lastValueFrom(
        this.http.post<{ pre_tokens?: number; message: string }>(
          `${ConfigService.muxUrl}/sessions/${sessionId}/compact`,
          body
        )
      );

      return result;
    } catch (error) {
      console.error('Failed to compact session:', error);
      throw error;
    }
  }

  /**
   * Pin a message to the session
   */
  async pinMessage(sessionId: string, messageId: string): Promise<string[]> {
    try {
      const result = await lastValueFrom(
        this.http.post<{ pinned_messages: string[] }>(
          `${ConfigService.muxUrl}/sessions/${sessionId}/pins/${messageId}`,
          {}
        )
      );
      return result.pinned_messages;
    } catch (error) {
      console.error('Failed to pin message:', error);
      throw error;
    }
  }

  /**
   * Unpin a message from the session
   */
  async unpinMessage(sessionId: string, messageId: string): Promise<string[]> {
    try {
      const result = await lastValueFrom(
        this.http.delete<{ pinned_messages: string[] }>(
          `${ConfigService.muxUrl}/sessions/${sessionId}/pins/${messageId}`
        )
      );
      return result.pinned_messages;
    } catch (error) {
      console.error('Failed to unpin message:', error);
      throw error;
    }
  }

  /**
   * Get all pinned messages for a session
   */
  async getPinnedMessages(sessionId: string): Promise<MuxSessionService.PinnedMessage[]> {
    try {
      const result = await lastValueFrom(
        this.http.get<{ pins: MuxSessionService.PinnedMessage[]; count: number }>(
          `${ConfigService.muxUrl}/sessions/${sessionId}/pins`
        )
      );
      return result.pins;
    } catch (error) {
      console.error('Failed to get pinned messages:', error);
      return [];
    }
  }

  // --- Channel Pins ---

  async pinChannelMessage(channelId: string, messageId: string, team?: string): Promise<string[]> {
    if (parsePlaceAddress(team)) return []; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/pins/${messageId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const result = await lastValueFrom(
        this.http.post<{ pinned_messages: string[] }>(url, {})
      );
      return result.pinned_messages;
    } catch (error) {
      console.error('Failed to pin channel message:', error);
      throw error;
    }
  }

  async unpinChannelMessage(channelId: string, messageId: string, team?: string): Promise<string[]> {
    if (parsePlaceAddress(team)) return []; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/pins/${messageId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const result = await lastValueFrom(
        this.http.delete<{ pinned_messages: string[] }>(url)
      );
      return result.pinned_messages;
    } catch (error) {
      console.error('Failed to unpin channel message:', error);
      throw error;
    }
  }

  async getKnowledgeGraph(channel?: string): Promise<any> {
    const params = channel ? `?channel=${encodeURIComponent(channel)}` : '';
    return lastValueFrom(this.http.get<any>(`${ConfigService.muxUrl}/knowledge-graph${params}`));
  }

  async tagMessage(ref: string, tag: string, action: 'add' | 'remove', team?: string): Promise<any> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      return await lastValueFrom(
        this.http.post<any>(`${ConfigService.muxUrl}/knowledge-graph/tag`, { ref, tag, action, team: team || 'local' })
      );
    } catch (error) {
      console.error('Failed to tag message:', error);
      throw error;
    }
  }

  async flagChannelMessage(channelId: string, messageId: string, team?: string): Promise<any> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/relay-queue/flag/${messageId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const result = await lastValueFrom(
        this.http.post<any>(url, {})
      );
      return result;
    } catch (error) {
      console.error('Failed to flag channel message:', error);
      throw error;
    }
  }

  async sendRawCommand(channelId: string, input: string, team?: string): Promise<any> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/raw-command`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      return await lastValueFrom(
        this.http.post<any>(url, { input })
      );
    } catch (error) {
      console.error('Failed to send raw command:', error);
      throw error;
    }
  }

  /** "Restart Session" (2026-09-10): end the channel's live CLI session; it resumes on the next message.
   *  Rejects with the HttpErrorResponse so callers can surface a 409 `detail` (agent mid-reply). */
  async restartChannelSession(channelId: string, team?: string): Promise<any> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    let url = `${ConfigService.muxUrl}/channels/${channelId}/restart-session`;
    if (team) url += `?team=${encodeURIComponent(team)}`;
    return await lastValueFrom(this.http.post<any>(url, {}));
  }

  /** "Restart All Sessions": end every live CLI session of the caller's agents; each resumes on its next message. */
  async restartAllSessions(): Promise<any> {
    return await lastValueFrom(this.http.post<any>(`${ConfigService.muxUrl}/sessions/restart-all`, {}));
  }

  async deleteChannelMessage(channelId: string, messageId: string, team?: string): Promise<void> {
    if (parsePlaceAddress(team)) return; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/messages/${messageId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      await lastValueFrom(
        this.http.delete(url)
      );
    } catch (error) {
      console.error('Failed to delete channel message:', error);
      throw error;
    }
  }

  async getChannelPinnedMessages(channelId: string, team?: string): Promise<MuxSessionService.PinnedMessage[]> {
    if (parsePlaceAddress(team)) return []; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/pins`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const result = await lastValueFrom(
        this.http.get<{ pins: MuxSessionService.PinnedMessage[]; count: number }>(url)
      );
      return result.pins;
    } catch (error) {
      console.error('Failed to get channel pinned messages:', error);
      return [];
    }
  }

  /**
   * Add or remove a reaction on a message.
   * If the same reactor+emoji already exists, removes it (toggle behavior).
   * Returns the updated reactions array.
   */
  async toggleReaction(
    sessionId: string,
    messageId: string,
    emoji: string,
    reactor: 'user' | 'assistant'
  ): Promise<{ emoji: string; reactor: 'user' | 'assistant'; timestamp: string }[]> {
    const response = await this.http.post<any>(
      `${ConfigService.muxUrl}/sessions/${sessionId}/messages/${messageId}/reactions`,
      { emoji, reactor }
    ).toPromise();
    return response.reactions;
  }

  /**
   * Toggle a reaction on a channel message (DM or personal channel).
   */
  async toggleChannelReaction(
    channelId: string,
    messageId: string,
    emoji: string,
    reactor: 'user' | 'assistant',
    team?: string
  ): Promise<{ emoji: string; reactor: 'user' | 'assistant'; timestamp: string }[]> {
    const response = await this.http.post<any>(
      `${ConfigService.muxUrl}/channels/${channelId}/messages/${messageId}/reactions`,
      { emoji, reactor, ...(team ? { team } : {}) }
    ).toPromise();
    return response.reactions;
  }

  // --- Channel navigation ---

  /**
   * Load the navigation tree (personal channels + DM channels)
   */
  /** The caller's attached desktops (Presence) — sources of remote places. */
  async listRemoteDevices(): Promise<MuxSessionService.RemoteDevice[]> {
    try {
      const resp = await lastValueFrom(
        this.http.get<{ devices: MuxSessionService.RemoteDevice[] }>(`${ConfigService.muxUrl}/remote/devices`));
      return resp?.devices || [];
    } catch {
      return [];
    }
  }

  /** Teams on one attached desktop, over the pipe. The registry only carries
   *  org ids; this has the display names AND the slugs — the token the
   *  desktop's own endpoints expect in a place address. */
  async listRemoteDeviceTeams(deviceId: string): Promise<{ slug: string; name: string }[]> {
    try {
      const resp = await lastValueFrom(
        this.http.get<{ teams: { id: string; slug: string; name: string }[] }>(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(deviceId)}/teams`));
      return (resp?.teams || []).map(t => ({ slug: t.slug || t.id, name: t.name || t.slug || t.id }));
    } catch {
      return [];
    }
  }

  async loadNavigation(team?: string): Promise<MuxSessionService.NavigationTree> {
    const remote = parsePlaceAddress(team);
    if (remote) {
      try {
        const resp = await lastValueFrom(this.http.get<{ channels: { id: string; name: string }[] }>(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(remote.device)}/channels?team=${encodeURIComponent(remote.team)}`));
        const channels = (resp?.channels || []).map(ch => ({
          id: ch.id, name: ch.name, type: 'personal', team_id: null, agent_id: null,
        }));
        return { personal_channels: channels, dm_channels: [], team: null };
      } catch (error) {
        console.error('Failed to load remote navigation:', error);
        return { personal_channels: [], dm_channels: [], team: null };
      }
    }
    try {
      let url = `${ConfigService.muxUrl}/navigation`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      return await lastValueFrom(
        this.http.get<MuxSessionService.NavigationTree>(url)
      );
    } catch (error) {
      console.error('Failed to load navigation:', error);
      return { personal_channels: [], dm_channels: [], team: null };
    }
  }

  /** Running background agents by channel id — the channel list's first paint;
   *  changes arrive as `channel_background_agents` telemetry. */
  async getBackgroundAgents(): Promise<Record<string, MuxSessionService.BackgroundAgent[]>> {
    try {
      const r = await lastValueFrom(
        this.http.get<{ channels: Record<string, MuxSessionService.BackgroundAgent[]> }>(
          `${ConfigService.muxUrl}/background-agents`
        )
      );
      return r?.channels || {};
    } catch {
      return {};
    }
  }

  /** A background agent's transcript after `after` (bytes). Throws on failure:
   *  the stream view shows the error instead of an empty transcript. */
  getBackgroundAgentTranscript(agentId: string, after: number): Promise<MuxSessionService.BackgroundAgentTranscript> {
    return lastValueFrom(
      this.http.get<MuxSessionService.BackgroundAgentTranscript>(
        `${ConfigService.muxUrl}/background-agents/${encodeURIComponent(agentId)}/transcript`,
        { params: { after: String(after) } }
      )
    );
  }

  /**
   * Create a personal channel
   */
  async createChannel(name: string, parentChannelId?: string, team?: string): Promise<MuxSessionService.NavChannel | null> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      const body: any = { name, parent_channel_id: parentChannelId };
      if (team) body.team = team;
      return await lastValueFrom(
        this.http.post<MuxSessionService.NavChannel>(
          `${ConfigService.muxUrl}/channels`,
          body
        )
      );
    } catch (error) {
      console.error('Failed to create channel:', error);
      return null;
    }
  }

  /** Folders under `path` — at the home, or on the linked computer `host` names. */
  async listDirectory(path: string = '~', host?: string): Promise<{path: string; items: {name: string; type: string}[]}> {
    try {
      const params: any = { path };
      if (host) params.host = host;
      return await lastValueFrom(
        this.http.get<{path: string; items: {name: string; type: string}[]}>(
          `${ConfigService.muxUrl}/fs/list`,
          { params }
        )
      );
    } catch (error) {
      console.error('Failed to list directory:', error);
      return { path, items: [] };
    }
  }

  /** Open a folder as a channel. `host` names the linked computer the folder is on
   *  (the channel gets affinity for it); empty = a folder on the home. */
  async openFolder(folderPath: string, name?: string, team?: string, host?: string): Promise<MuxSessionService.NavChannel | null> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      const body: any = { folder_path: folderPath, name };
      if (team) body.team = team;
      if (host) body.host = host;
      return await lastValueFrom(
        this.http.post<MuxSessionService.NavChannel>(
          `${ConfigService.muxUrl}/channels/open-folder`,
          body
        )
      );
    } catch (error: any) {
      const detail = error?.error?.detail || 'Failed to open folder';
      console.error('Failed to open folder:', error);
      throw new Error(detail);
    }
  }

  /**
   * Archive a child work session and post a summary to the parent channel
   */
  async archiveWorkSession(channelId: string, sessionId: string, team?: string): Promise<boolean> {
    if (parsePlaceAddress(team)) return false; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/work-sessions/${sessionId}/archive`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      await lastValueFrom(
        this.http.post<any>(url, {})
      );
      return true;
    } catch (error) {
      console.error('Failed to archive work session:', error);
      return false;
    }
  }

  /**
   * Create a work session linked to a channel
   */
  async createWorkSession(channelId: string, title: string, agentId: string, warmUpPrompt?: string, team?: string): Promise<MuxSessionService.WorkSession | null> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      const result = await lastValueFrom(
        this.http.post<any>(
          `${ConfigService.muxUrl}/channels/${channelId}/work-sessions`,
          { title, agent_id: agentId, warm_up_prompt: warmUpPrompt, ...(team ? { team } : {}) }
        )
      );
      return result;
    } catch (error) {
      console.error('Failed to create work session:', error);
      return null;
    }
  }

  /**
   * Delete a personal channel
   */
  async deleteChannel(channelId: string, team?: string, purge = false): Promise<boolean> {
    if (parsePlaceAddress(team)) return false; // not supported over the pipe (v1)
    try {
      // purge = also remove the agent's transcript/memory copies of the channel
      // folder and its config entries on the server, audited (contractual deletion).
      let url = `${ConfigService.muxUrl}/channels/${channelId}?team=${encodeURIComponent(team || 'local')}`;
      if (purge) url += '&purge=true';
      await lastValueFrom(
        this.http.delete(url)
      );
      return true;
    } catch (error: any) {
      // 423 = refused (legal hold): the caller shows the server's reason.
      if (error?.status === 423) throw error;
      console.error('Failed to delete channel:', error);
      return false;
    }
  }

  async updateChannelAgent(channelId: string, agentId: string | null, team?: string): Promise<boolean> {
    const remote = parsePlaceAddress(team);
    if (remote) {
      // Assign the answering agent on the desktop's channel (portal agent tab).
      try {
        await lastValueFrom(this.http.post(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(remote.device)}/channels/${encodeURIComponent(channelId)}/agent`,
          { team: remote.team, agent_id: agentId || '' }));
        return true;
      } catch (error) {
        console.error('Failed to update remote channel agent:', error);
        return false;
      }
    }
    try {
      const body: any = { agent_id: agentId };
      if (team) body.team = team;
      await lastValueFrom(
        this.http.patch(`${ConfigService.muxUrl}/channels/${channelId}/config`, body)
      );
      return true;
    } catch (error) {
      console.error('Failed to update channel agent:', error);
      return false;
    }
  }

  async renameChannel(channelId: string, newName: string, team?: string): Promise<boolean> {
    if (parsePlaceAddress(team)) return false; // not supported over the pipe (v1)
    try {
      const body: any = { name: newName };
      if (team) body.team = team;
      await lastValueFrom(
        this.http.post(`${ConfigService.muxUrl}/channels/${channelId}/rename`, body)
      );
      return true;
    } catch (error) {
      console.error('Failed to rename channel:', error);
      return false;
    }
  }

  async archiveChannel(channelId: string, team?: string): Promise<boolean> {
    if (parsePlaceAddress(team)) return false; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      await lastValueFrom(
        this.http.patch(url, {})
      );
      return true;
    } catch (error) {
      console.error('Failed to archive channel:', error);
      return false;
    }
  }

  async unarchiveChannel(channelId: string, team?: string): Promise<boolean> {
    if (parsePlaceAddress(team)) return false; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/unarchive`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      await lastValueFrom(
        this.http.post(url, {})
      );
      return true;
    } catch (error) {
      console.error('Failed to unarchive channel:', error);
      return false;
    }
  }

  /**
   * Get unread message counts for all channels (for badge notifications)
   */
  async getUnreadCounts(team?: string): Promise<{ channels: { id: string; name: string; unreadCount: number; lastMessage: any }[] }> {
    if (parsePlaceAddress(team)) return { channels: [] }; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/unread`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      return await lastValueFrom(
        this.http.get<any>(url)
      );
    } catch (error) {
      console.error('Failed to load unread counts:', error);
      return { channels: [] };
    }
  }

  /**
   * Mark messages in a channel as read
   */
  async markChannelRead(channelId: string, messageId?: string, team?: string): Promise<{ marked: number }> {
    if (parsePlaceAddress(team)) return { marked: 0 }; // not supported over the pipe (v1)
    try {
      return await lastValueFrom(
        this.http.post<{ marked: number }>(
          `${ConfigService.muxUrl}/channels/${channelId}/mark-read${team ? '?team=' + encodeURIComponent(team) : ''}`,
          messageId ? { messageId } : {}
        )
      );
    } catch (error) {
      console.error('Failed to mark channel read:', error);
      return { marked: 0 };
    }
  }

  /**
   * The per-(channel, agent) model override, or null to use the agent default.
   */
  async getChannelModelOverride(channelId: string, agentId: string, team?: string): Promise<string | null> {
    if (parsePlaceAddress(team)) return null; // not supported over the pipe (v1)
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/model-override?agent_id=${encodeURIComponent(agentId)}`;
      if (team) url += `&team=${encodeURIComponent(team)}`;
      const resp = await lastValueFrom(this.http.get<{ model: string | null }>(url));
      return resp?.model ?? null;
    } catch {
      return null;
    }
  }

  /** The hosts this node knows (hosts.json) with the caller's liveness on each, plus the home's name. */
  async listAffinityHosts(): Promise<{ home: string; hosts: { name: string; address: string; port: number; location: string; alive: boolean }[] }> {
    try {
      return await lastValueFrom(this.http.get<{ home: string; hosts: any[] }>(`${ConfigService.muxUrl}/hosts`));
    } catch {
      return { home: '', hosts: [] };
    }
  }

  /** This channel's host affinity: an ordered list of host names; the home is implicit last. */
  async getChannelAffinity(channelId: string, team?: string): Promise<{ hosts: string[]; home: string }> {
    if (parsePlaceAddress(team)) return { hosts: [], home: '' };
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/affinity`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      const resp = await lastValueFrom(this.http.get<{ hosts: string[]; home: string }>(url));
      return { hosts: resp?.hosts || [], home: resp?.home || '' };
    } catch {
      return { hosts: [], home: '' };
    }
  }

  /** Set this channel's host affinity ([] = home only). Rejects unknown host names (400). */
  async setChannelAffinity(channelId: string, hosts: string[], team?: string): Promise<{ hosts: string[]; home: string }> {
    if (parsePlaceAddress(team)) return { hosts: [], home: '' };
    return await lastValueFrom(
      this.http.put<{ hosts: string[]; home: string }>(`${ConfigService.muxUrl}/channels/${channelId}/affinity`,
        { hosts, team: team || 'local' })
    );
  }

  /** Set (empty clears → revert to agent default) this channel's model for one agent. */
  async setChannelModelOverride(channelId: string, agentId: string, model: string, team?: string): Promise<void> {
    if (parsePlaceAddress(team)) return; // not supported over the pipe (v1)
    await lastValueFrom(
      this.http.post(`${ConfigService.muxUrl}/channels/${channelId}/model-override`,
        { agent_id: agentId, model, team: team || 'local' })
    );
  }

  /**
   * Get messages for a channel
   */
  async getChannelMessages(channelId: string, limit = 50, since?: string, team?: string): Promise<MuxSessionService.ChannelMessage[]> {
    const remote = parsePlaceAddress(team);
    if (remote) {
      // Remote page op has no `since` cursor — always the recent page. The
      // messages come from the same lit stack on the device, so the shape is
      // already ChannelMessage.
      try {
        const resp = await lastValueFrom(this.http.get<{ messages: MuxSessionService.ChannelMessage[] }>(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(remote.device)}/channels/${encodeURIComponent(channelId)}/messages?team=${encodeURIComponent(remote.team)}&limit=${limit}`));
        return resp?.messages || [];
      } catch (error) {
        console.error('Failed to load remote channel messages:', error);
        return [];
      }
    }
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/messages?limit=${limit}`;
      if (since) url += `&since=${encodeURIComponent(since)}`;
      if (team) url += `&team=${encodeURIComponent(team)}`;
      const resp = await lastValueFrom(
        this.http.get<{ messages: MuxSessionService.ChannelMessage[] }>(url)
      );
      return resp.messages || [];
    } catch (error) {
      console.error('Failed to load channel messages:', error);
      return [];
    }
  }

  /**
   * Get a single channel message by ID (full untruncated content)
   */
  async getChannelMessage(channelId: string, messageId: string, team?: string): Promise<MuxSessionService.ChannelMessage | null> {
    if (parsePlaceAddress(team)) return null; // remote page content is never truncated
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/messages/${messageId}`;
      if (team) url += `?team=${encodeURIComponent(team)}`;
      return await lastValueFrom(
        this.http.get<MuxSessionService.ChannelMessage>(url)
      );
    } catch (error) {
      console.error('Failed to load channel message:', error);
      return null;
    }
  }

  /**
   * Post a message to a channel
   */
  async postChannelMessage(channelId: string, content: string, team?: string, source?: string): Promise<MuxSessionService.ChannelMessage | null> {
    const remote = parsePlaceAddress(team);
    if (remote) {
      try {
        const resp = await lastValueFrom(this.http.post<{ ok: boolean; result?: { id?: string } }>(
          `${ConfigService.muxUrl}/remote/${encodeURIComponent(remote.device)}/channels/${encodeURIComponent(channelId)}/messages`,
          { team: remote.team, content }));
        if (!resp?.ok) return null;
        // The pipe's send op doesn't return the persisted message — synthesize
        // enough for the caller's ack path (a real id arrives on the next page).
        return {
          id: resp.result?.id || `remote-${Date.now()}`,
          content, timestamp: new Date().toISOString(),
          direction: 'in', role: 'user', from: 'me',
        };
      } catch (error) {
        console.error('Failed to post remote channel message:', error);
        return null;
      }
    }
    try {
      return await lastValueFrom(
        this.http.post<MuxSessionService.ChannelMessage>(
          `${ConfigService.muxUrl}/channels/${channelId}/messages`,
          { content, ...(team ? { team } : {}), ...(source ? { source } : {}) }
        )
      );
    } catch (error) {
      console.error('Failed to post channel message:', error);
      return null;
    }
  }

  // --- Virtual scroll / seekable timeline ---

  async getChannelMessagesByIndex(channelId: string, start: number, count: number, team?: string): Promise<{ messages: MuxSessionService.ChannelMessage[]; total: number }> {
    const remoteAddr = parsePlaceAddress(team);
    if (remoteAddr) {
      // No seekable timeline over the pipe (v1) — the recent page is the
      // whole window, so history browsing is bounded on remote places.
      const messages = await this.getChannelMessages(channelId, count, undefined, team);
      return { messages, total: messages.length };
    }
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/messages/by-index?start=${start}&count=${count}`;
      if (team) url += `&team=${encodeURIComponent(team)}`;
      return await lastValueFrom(
        this.http.get<{ messages: MuxSessionService.ChannelMessage[]; total: number }>(url)
      );
    } catch (error) {
      console.error('Failed to load messages by index:', error);
      return { messages: [], total: 0 };
    }
  }

  async findChannelMessageIndex(channelId: string, messageId: string, team?: string): Promise<number | null> {
    if (parsePlaceAddress(team)) return null;
    try {
      let url = `${ConfigService.muxUrl}/channels/${channelId}/messages/find-index?message_id=${encodeURIComponent(messageId)}`;
      if (team) url += `&team=${encodeURIComponent(team)}`;
      const resp = await lastValueFrom(
        this.http.get<{ message_id: string; index: number }>(url)
      );
      return resp.index;
    } catch (error) {
      console.error('Failed to find message index:', error);
      return null;
    }
  }

  /**
   * Get time ago string for a date
   */
  getTimeAgo(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }
}
